import { Alert } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import { Navigate } from 'react-router-dom';
import { CreatePhysicalMeetingStepsIndex, CreatePhysicalMeetingWithEmailConfigurationStepsIndex } from './CreatePhysicalMeeting.types';
import DetailsStep from './Steps/DetailsStep/DetailsStep';
import PublicationStep from './Steps/PublicationStep/PublicationStep';
import { eventsRoute } from 'basics/constants/routes.constants';
import {
  FormLayoutContainer,
  FormLayoutMidPanelHeader,
  FormLayoutRightPanel,
  FormLayoutMidPanel,
  FormLayoutMidPanelContent,
} from 'components/FormLayout';
import LargeContainer from 'components/Layout/LargeContainer';
import { EventStatus, PhysicalEvent } from 'generated/event.types';
import EditEventButtonsBar from 'modules/events/components/EditEventButtonsBar';
import SideFormDetails from 'modules/events/components/SideFormDetails';
import useCreatePhysicalMeeting from 'modules/events/routes/CreatePhysicalMeeting/CreatePhysicalMeeting.hook';
import CommunicationStep from 'modules/events/routes/CreatePhysicalMeeting/Steps/CommunicationStep';
import EmailConfigurationStep from 'modules/events/routes/CreatePhysicalMeeting/Steps/EmailConfigurationStep/EmailConfigurationStep';
import Box from 'yoda-ui/components/Box';
import Icon from 'yoda-ui/components/Icons/Icon';
import { Icons } from 'yoda-ui/components/Icons/Icon/Icon';
import LinkButton from 'yoda-ui/components/Links/LinkButton/LinkButton';
import Loader from 'yoda-ui/components/Loader';
import Stepper from 'yoda-ui/components/Stepper/Stepper';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

const CreatePhysicalMeeting: FC = () => {
  const flags = useFlags();
  const {
    t,
    handleClickHome,
    handleChangeStep,
    showLoader,
    yodaFormMethods,
    stepIndexer,
    id,
    buttonsConfig,
    actionStates,
    stepsList,
    event,
  } = useCreatePhysicalMeeting();

  if (!id) {
    return <Navigate to={ eventsRoute } replace />;
  }

  if (showLoader) {
    return <Box display="flex" flexDirection="column" justifyContent="center" width="100vw"><Loader center /></Box>;
  }

  // TODO: keep this block when epic BIOGENLINC-3524 is finished
  const stepsWithEmailConfiguration = (
    <>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingWithEmailConfigurationStepsIndex.detailsStep }>
        <DetailsStep />
      </Box>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingWithEmailConfigurationStepsIndex.communicationStep }>
        <CommunicationStep />
      </Box>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingWithEmailConfigurationStepsIndex.emailConfigurationStep }>
        <EmailConfigurationStep />
      </Box>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingWithEmailConfigurationStepsIndex.publicationStep }>
        <PublicationStep />
      </Box>
    </>
  );

  // TODO: remove this block when epic BIOGENLINC-3524 is finished
  const stepsConfiguration = (
    <>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingStepsIndex.detailsStep }>
        <DetailsStep />
      </Box>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingStepsIndex.communicationStep }>
        <CommunicationStep />
      </Box>
      <Box hidden={ stepIndexer.stepIndex !== CreatePhysicalMeetingStepsIndex.publicationStep }>
        <PublicationStep />
      </Box>
    </>
  );

  return (
    <LargeContainer
      paddingRight={ YodaSpacing.xLarge }
      paddingLeft={ YodaSpacing.xLarge }
    >
      <YodaFormProvider { ...yodaFormMethods.providerFields }>
        <FormLayoutContainer>
          <FormLayoutMidPanel >
            <FormLayoutMidPanelHeader>
              <Box marginBottom={ YodaSpacing.medium }>
                <LinkButton startIcon={ <Icon name={ Icons.chevronLeft } /> } onClick={ handleClickHome }>
                  { `${t('home_link')}` }
                </LinkButton>
              </Box>

              <Stepper
                handleChangeStep={ handleChangeStep }
                stepsList={ stepsList }
                stepIndex={ stepIndexer.stepIndex }
              />
            </FormLayoutMidPanelHeader>
            <FormLayoutMidPanelContent>
              {
                (event?.status === EventStatus.published && (event?.data as PhysicalEvent)?.biogenLincConfig?.showSendInvitations
                  && !(event?.data as PhysicalEvent).invitationSent) ? (
                    <Alert severity={ 'warning' }>
                      { t('event_publish_invitation_not_sent_yet_label') }
                    </Alert>
                  ) : null
              }

              { flags.eventsBiogenlinc4151SectionEmail ? stepsWithEmailConfiguration : stepsConfiguration }

              <EditEventButtonsBar
                buttonsConfig={ buttonsConfig }
                actionStates={ actionStates }
              />
            </FormLayoutMidPanelContent>
          </FormLayoutMidPanel>
          <FormLayoutRightPanel >
            <SideFormDetails />
          </FormLayoutRightPanel>
        </FormLayoutContainer>
      </YodaFormProvider>
    </LargeContainer>
  );
};

export default CreatePhysicalMeeting;
